import type { AcademicLevelConfig, AcademicLevel, EducationSystem, MathTopic, Difficulty, QuestionType } from "./types";

const allTopics: MathTopic[] = [
  "arithmetic",
  "algebra",
  "geometry",
  "trigonometry",
  "calculus",
  "statistics",
  "probability",
  "discrete-mathematics",
  "linear-algebra",
  "real-analysis",
  "complex-analysis",
  "abstract-algebra",
  "number-theory",
  "topology",
  "differential-geometry",
  "functional-analysis",
  "measure-theory",
  "partial-differential-equations",
  "numerical-analysis",
  "mathematical-optimization",
  "operations-research",
  "mathematical-logic",
  "advanced-probability",
  "advanced-statistics",
];

const advancedDifficulties: Difficulty[] = ["easy", "medium", "hard", "expert"];

const baseQuestionTypes: QuestionType[] = [
  "multiple-choice",
  "short-answer",
  "numerical-answer",
  "fill-in-the-blank",
  "true-false",
  "problem-solving",
  "word-problem",
  "calculation",
  "conceptual",
  "mixed",
];

const advancedQuestionTypes: QuestionType[] = [
  ...baseQuestionTypes,
  "proof",
  "derivation",
];

export const academicLevels: AcademicLevelConfig[] = [
  // India levels
  {
    id: "class-1",
    name: "Class 1",
    description: "Foundational arithmetic and number sense",
    educationSystem: "india",
    topics: ["arithmetic"],
    difficulties: ["easy"],
    questionTypes: ["multiple-choice", "fill-in-the-blank", "true-false", "numerical-answer"],
  },
  {
    id: "class-2",
    name: "Class 2",
    description: "Basic operations, place value, simple word problems",
    educationSystem: "india",
    topics: ["arithmetic"],
    difficulties: ["easy"],
    questionTypes: ["multiple-choice", "fill-in-the-blank", "true-false", "numerical-answer", "word-problem"],
  },
  {
    id: "class-3",
    name: "Class 3",
    description: "Multiplication, division, fractions introduction",
    educationSystem: "india",
    topics: ["arithmetic"],
    difficulties: ["easy", "medium"],
    questionTypes: baseQuestionTypes,
  },
  {
    id: "class-4",
    name: "Class 4",
    description: "Multi-digit operations, factors, multiples, basic geometry",
    educationSystem: "india",
    topics: ["arithmetic", "geometry"],
    difficulties: ["easy", "medium"],
    questionTypes: baseQuestionTypes,
  },
  {
    id: "class-5",
    name: "Class 5",
    description: "Decimals, fractions, area, perimeter, data handling",
    educationSystem: "india",
    topics: ["arithmetic", "geometry", "statistics"],
    difficulties: ["easy", "medium"],
    questionTypes: baseQuestionTypes,
  },
  {
    id: "class-6",
    name: "Class 6",
    description: "Integers, algebra basics, ratio, proportion, geometry",
    educationSystem: "india",
    topics: ["arithmetic", "algebra", "geometry", "statistics"],
    difficulties: ["easy", "medium"],
    questionTypes: baseQuestionTypes,
  },
  {
    id: "class-7",
    name: "Class 7",
    description: "Rational numbers, algebraic expressions, lines, angles, triangles",
    educationSystem: "india",
    topics: ["arithmetic", "algebra", "geometry", "statistics", "probability"],
    difficulties: ["easy", "medium"],
    questionTypes: baseQuestionTypes,
  },
  {
    id: "class-8",
    name: "Class 8",
    description: "Linear equations, quadrilaterals, data handling, squares, cubes",
    educationSystem: "india",
    topics: ["arithmetic", "algebra", "geometry", "statistics", "probability"],
    difficulties: ["easy", "medium", "hard"],
    questionTypes: baseQuestionTypes,
  },
  {
    id: "class-9",
    name: "Class 9",
    description: "Number systems, polynomials, coordinate geometry, Euclid geometry",
    educationSystem: "india",
    topics: ["arithmetic", "algebra", "geometry", "trigonometry", "statistics", "probability"],
    difficulties: ["easy", "medium", "hard"],
    questionTypes: baseQuestionTypes,
  },
  {
    id: "class-10",
    name: "Class 10",
    description: "Real numbers, quadratic equations, trigonometry, circles, statistics",
    educationSystem: "india",
    topics: ["arithmetic", "algebra", "geometry", "trigonometry", "statistics", "probability"],
    difficulties: ["easy", "medium", "hard"],
    questionTypes: baseQuestionTypes,
  },
  {
    id: "class-11",
    name: "Class 11",
    description: "Sets, relations, functions, trigonometric functions, limits, derivatives",
    educationSystem: "india",
    topics: ["algebra", "trigonometry", "calculus", "statistics", "probability", "discrete-mathematics", "linear-algebra"],
    difficulties: ["easy", "medium", "hard"],
    questionTypes: [...baseQuestionTypes, "proof", "derivation"],
  },
  {
    id: "class-12",
    name: "Class 12",
    description: "Calculus, vectors, 3D geometry, linear programming, probability",
    educationSystem: "india",
    topics: ["algebra", "calculus", "geometry", "trigonometry", "statistics", "probability", "linear-algebra", "discrete-mathematics"],
    difficulties: ["easy", "medium", "hard"],
    questionTypes: [...baseQuestionTypes, "proof", "derivation"],
  },
  {
    id: "jee-foundation",
    name: "JEE Foundation",
    description: "Conceptual foundation for JEE preparation (Class 9-10 level)",
    educationSystem: "india",
    topics: ["algebra", "geometry", "trigonometry", "calculus"],
    difficulties: ["medium", "hard"],
    questionTypes: ["multiple-choice", "numerical-answer", "problem-solving", "conceptual"],
  },
  {
    id: "jee-main",
    name: "JEE Main",
    description: "JEE Main level mathematics (Class 11-12)",
    educationSystem: "india",
    topics: ["algebra", "calculus", "geometry", "trigonometry", "statistics", "probability", "linear-algebra", "discrete-mathematics"],
    difficulties: ["medium", "hard"],
    questionTypes: ["multiple-choice", "numerical-answer", "problem-solving"],
  },
  {
    id: "jee-advanced",
    name: "JEE Advanced",
    description: "JEE Advanced level mathematics (advanced Class 11-12)",
    educationSystem: "india",
    topics: ["algebra", "calculus", "geometry", "trigonometry", "statistics", "probability", "linear-algebra", "discrete-mathematics"],
    difficulties: ["hard", "expert"],
    questionTypes: ["multiple-choice", "numerical-answer", "problem-solving", "proof", "derivation"],
  },
  {
    id: "neet",
    name: "NEET Mathematics",
    description: "Mathematics for NEET (where applicable)",
    educationSystem: "india",
    topics: ["algebra", "calculus", "geometry", "trigonometry", "statistics", "probability"],
    difficulties: ["easy", "medium", "hard"],
    questionTypes: ["multiple-choice", "numerical-answer", "problem-solving"],
  },
  {
    id: "state-competitive",
    name: "State Competitive Exams",
    description: "State-level engineering/medical entrance mathematics",
    educationSystem: "india",
    topics: ["algebra", "calculus", "geometry", "trigonometry", "statistics", "probability"],
    difficulties: ["easy", "medium", "hard"],
    questionTypes: ["multiple-choice", "short-answer", "numerical-answer", "problem-solving"],
  },
  {
    id: "undergraduate",
    name: "Undergraduate",
    description: "University-level mathematics (BSc, BTech, etc.)",
    educationSystem: "india",
    topics: allTopics,
    difficulties: advancedDifficulties,
    questionTypes: advancedQuestionTypes,
  },
  {
    id: "postgraduate",
    name: "Postgraduate",
    description: "Master's level mathematics (MSc, MTech, etc.)",
    educationSystem: "india",
    topics: allTopics.filter(t => !["arithmetic"].includes(t)),
    difficulties: advancedDifficulties,
    questionTypes: advancedQuestionTypes,
  },
  {
    id: "advanced",
    name: "Advanced Mathematics",
    description: "Research-level and beyond standard curriculum",
    educationSystem: "india",
    topics: allTopics.filter(t => !["arithmetic", "algebra", "geometry", "trigonometry", "statistics", "probability"].includes(t)),
    difficulties: ["hard", "expert"],
    questionTypes: ["proof", "derivation", "problem-solving", "conceptual"],
  },

  // US levels
  {
    id: "elementary",
    name: "Elementary (K-5)",
    description: "Foundational arithmetic, fractions, basic geometry",
    educationSystem: "us",
    topics: ["arithmetic", "geometry"],
    difficulties: ["easy", "medium"],
    questionTypes: baseQuestionTypes,
  },
  {
    id: "middle-school",
    name: "Middle School (6-8)",
    description: "Pre-algebra, integers, ratios, basic statistics, geometry",
    educationSystem: "us",
    topics: ["arithmetic", "algebra", "geometry", "statistics", "probability"],
    difficulties: ["easy", "medium"],
    questionTypes: baseQuestionTypes,
  },
  {
    id: "grade-9",
    name: "Grade 9 (Algebra I)",
    description: "Linear equations, functions, polynomials, basic geometry",
    educationSystem: "us",
    topics: ["algebra", "geometry", "statistics", "probability"],
    difficulties: ["easy", "medium", "hard"],
    questionTypes: baseQuestionTypes,
  },
  {
    id: "grade-10",
    name: "Grade 10 (Geometry)",
    description: "Euclidean geometry, proofs, coordinate geometry, trigonometry basics",
    educationSystem: "us",
    topics: ["algebra", "geometry", "trigonometry", "statistics", "probability"],
    difficulties: ["easy", "medium", "hard"],
    questionTypes: [...baseQuestionTypes, "proof"],
  },
  {
    id: "grade-11",
    name: "Grade 11 (Algebra II / Precalculus)",
    description: "Advanced functions, trigonometry, sequences, series, limits",
    educationSystem: "us",
    topics: ["algebra", "geometry", "trigonometry", "calculus", "statistics", "probability", "discrete-mathematics"],
    difficulties: ["easy", "medium", "hard"],
    questionTypes: [...baseQuestionTypes, "proof", "derivation"],
  },
  {
    id: "grade-12",
    name: "Grade 12 (Calculus / Statistics)",
    description: "Differential/integral calculus, AP Statistics, advanced topics",
    educationSystem: "us",
    topics: ["algebra", "calculus", "statistics", "probability", "linear-algebra", "discrete-mathematics"],
    difficulties: ["easy", "medium", "hard"],
    questionTypes: [...baseQuestionTypes, "proof", "derivation"],
  },
  {
    id: "ap-level",
    name: "AP Level (Calculus AB/BC, Statistics)",
    description: "Advanced Placement mathematics curriculum",
    educationSystem: "us",
    topics: ["calculus", "statistics", "probability"],
    difficulties: ["medium", "hard", "expert"],
    questionTypes: ["multiple-choice", "short-answer", "problem-solving", "proof", "derivation", "word-problem"],
  },
  {
    id: "college",
    name: "College / Undergraduate",
    description: "University mathematics (Calculus, Linear Algebra, Discrete Math, etc.)",
    educationSystem: "us",
    topics: allTopics,
    difficulties: advancedDifficulties,
    questionTypes: advancedQuestionTypes,
  },
  {
    id: "graduate",
    name: "Graduate",
    description: "Graduate-level mathematics (Master's, PhD)",
    educationSystem: "us",
    topics: allTopics.filter(t => !["arithmetic", "algebra", "geometry", "trigonometry", "statistics", "probability"].includes(t)),
    difficulties: ["hard", "expert"],
    questionTypes: ["proof", "derivation", "problem-solving", "conceptual"],
  },

  // General levels
  {
    id: "elementary",
    name: "Elementary",
    description: "Basic arithmetic and geometry concepts",
    educationSystem: "general",
    topics: ["arithmetic", "geometry"],
    difficulties: ["easy", "medium"],
    questionTypes: baseQuestionTypes,
  },
  {
    id: "middle-school",
    name: "Middle School",
    description: "Pre-algebra, basic statistics, geometry",
    educationSystem: "general",
    topics: ["arithmetic", "algebra", "geometry", "statistics", "probability"],
    difficulties: ["easy", "medium"],
    questionTypes: baseQuestionTypes,
  },
  {
    id: "grade-9",
    name: "High School",
    description: "Algebra, geometry, trigonometry, precalculus",
    educationSystem: "general",
    topics: ["algebra", "geometry", "trigonometry", "calculus", "statistics", "probability", "discrete-mathematics"],
    difficulties: ["easy", "medium", "hard"],
    questionTypes: [...baseQuestionTypes, "proof", "derivation"],
  },
  {
    id: "college",
    name: "Undergraduate",
    description: "University-level mathematics",
    educationSystem: "general",
    topics: allTopics,
    difficulties: advancedDifficulties,
    questionTypes: advancedQuestionTypes,
  },
  {
    id: "graduate",
    name: "Postgraduate",
    description: "Advanced university mathematics",
    educationSystem: "general",
    topics: allTopics.filter(t => !["arithmetic"].includes(t)),
    difficulties: advancedDifficulties,
    questionTypes: advancedQuestionTypes,
  },
  {
    id: "advanced",
    name: "Advanced",
    description: "Research-level mathematics",
    educationSystem: "general",
    topics: allTopics.filter(t => !["arithmetic", "algebra", "geometry", "trigonometry", "statistics", "probability"].includes(t)),
    difficulties: ["hard", "expert"],
    questionTypes: ["proof", "derivation", "problem-solving", "conceptual"],
  },
];

export function getAcademicLevel(id: string): AcademicLevelConfig | undefined {
  return academicLevels.find((l) => l.id === id);
}

export function getLevelsForSystem(system: EducationSystem): AcademicLevelConfig[] {
  return academicLevels.filter((l) => l.educationSystem === system);
}

export function getLevelNamesForSystem(system: EducationSystem): { id: AcademicLevel; name: string }[] {
  return getLevelsForSystem(system).map((l) => ({ id: l.id, name: l.name }));
}